import React, { useRef, useState, useEffect } from "react";
import { FaTimes, FaImage, FaUpload, FaTrash, FaPaintBrush, FaEraser, FaSyncAlt, FaCloudUploadAlt, FaCheckCircle } from "react-icons/fa";
import ProductMaskEditor from "../ProductMaskEditor";

const ReferenceImageModal = ({
  isOpen,
  onClose,
  textboxUploadedImages = [],
  textboxUploadError,
  handleTextboxFileUpload,
  removeTextboxImage,
  textboxFileInputRef,
  isPreview = false,
  className = "",
  setTextboxUploadedImages,
  disableMasking = false,
  // Instagram post type to conditionally hide features
  instagramPostType,
}) => {
  const replaceFileInputRef = useRef(null);
  const [isMaskEditorOpen, setIsMaskEditorOpen] = useState(false);
  const [maskedPreviewUrl, setMaskedPreviewUrl] = useState(null);
  
  const hasImage = textboxUploadedImages.length > 0;


  const handleReplace = () => {
    // Trigger the replace file input
    replaceFileInputRef?.current?.click();
  };

  const handleReplaceFileUpload = (e) => {
    const files = Array.from(e.target.files);
    
    if (files.length === 0) return;
    
    // First remove the existing image
    if (hasImage) {
      removeTextboxImage(0);
    }
    
    // Then add the new image using the original upload handler
    // We need to create a synthetic event that mimics the original file input
    const syntheticEvent = {
      target: {
        files: files
      }
    };
    
    // Call the original handler with the new file
    handleTextboxFileUpload(syntheticEvent);
    
    // Clear the replace input
    if (replaceFileInputRef.current) {
      replaceFileInputRef.current.value = "";
    }
  };

  const handleRemove = () => {
    // Remove the current image
    removeTextboxImage(0);
  };

  const handleClearMask = () => {
    if (!hasImage || typeof setTextboxUploadedImages !== 'function') return;
    setTextboxUploadedImages((prev) => {
      if (!Array.isArray(prev) || prev.length === 0) return prev;
      const next = [...prev];
      if (next[0]) {
        const { maskBlob, maskOriginalSize, ...rest } = next[0];
        next[0] = rest;
      }
      return next;
    });
    setMaskedPreviewUrl(null);
  };

  const handleMaskConfirm = async ({ maskBlob, originalSize }) => {
    if (!hasImage || typeof setTextboxUploadedImages !== 'function') {
      setIsMaskEditorOpen(false);
      return;
    }
    setTextboxUploadedImages((prev) => {
      if (!Array.isArray(prev) || prev.length === 0) return prev;
      const next = [...prev];
      next[0] = { ...next[0], maskBlob, maskOriginalSize: originalSize };
      return next;
    });
    setIsMaskEditorOpen(false);
  };

  const hasMask = !!textboxUploadedImages[0]?.maskBlob;

  // Cleanup body overflow when modal closes
  useEffect(() => {
    return () => {
      document.body.style.overflow = '';
    };
  }, []);

  // Build a masked preview (image + dark overlay with mask punched out) when mask exists
  useEffect(() => {
    let isCancelled = false;
    let maskObjectUrl = null;

    const buildMaskedPreview = async () => {
      const baseUrl = textboxUploadedImages[0]?.preview;
      const maskBlob = textboxUploadedImages[0]?.maskBlob;
      if (!hasImage || !hasMask || !baseUrl || !maskBlob) {
        setMaskedPreviewUrl(null);
        return;
      }

      try {
        // Load base image
        const baseImg = new Image();
        baseImg.crossOrigin = 'anonymous';
        const baseImgLoad = new Promise((resolve, reject) => {
          baseImg.onload = () => resolve();
          baseImg.onerror = reject;
        });
        baseImg.src = baseUrl;

        // Load mask image from blob
        maskObjectUrl = URL.createObjectURL(maskBlob);
        const maskImg = new Image();
        maskImg.crossOrigin = 'anonymous';
        const maskImgLoad = new Promise((resolve, reject) => {
          maskImg.onload = () => resolve();
          maskImg.onerror = reject;
        });
        maskImg.src = maskObjectUrl;

        await Promise.all([baseImgLoad, maskImgLoad]);
        if (isCancelled) return;

        // Detect if mask actually has any painted pixels (alpha > 0)
        const detectSize = 128;
        const detectCanvas = document.createElement('canvas');
        detectCanvas.width = detectSize;
        detectCanvas.height = detectSize;
        const dctx = detectCanvas.getContext('2d');
        dctx.clearRect(0, 0, detectSize, detectSize);
        dctx.drawImage(maskImg, 0, 0, detectSize, detectSize);
        const data = dctx.getImageData(0, 0, detectSize, detectSize).data;
        let hasPaint = false;
        for (let i = 3; i < data.length; i += 4) {
          if (data[i] > 5) { // any visible alpha
            hasPaint = true;
            break;
          }
        }
        if (!hasPaint) {
          setMaskedPreviewUrl(null);
          return;
        }

        // Build masked preview: image with dim overlay punched out by mask
        const w = baseImg.naturalWidth || baseImg.width;
        const h = baseImg.naturalHeight || baseImg.height;
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        const ctx = canvas.getContext('2d');

        // Draw original image
        ctx.clearRect(0, 0, w, h);
        ctx.drawImage(baseImg, 0, 0, w, h);

        // Create overlay and punch out mask
        const overlay = document.createElement('canvas');
        overlay.width = w;
        overlay.height = h;
        const octx = overlay.getContext('2d');
        octx.clearRect(0, 0, w, h);
        octx.fillStyle = 'rgba(0,0,0,0.5)';
        octx.fillRect(0, 0, w, h);
        octx.globalCompositeOperation = 'destination-out';
        octx.imageSmoothingEnabled = false;
        // Scale mask to match base image size
        const mw = maskImg.naturalWidth || maskImg.width;
        const mh = maskImg.naturalHeight || maskImg.height;
        octx.drawImage(maskImg, 0, 0, mw, mh, 0, 0, w, h);

        // Merge overlay onto image
        ctx.drawImage(overlay, 0, 0);
        
        const dataUrl = canvas.toDataURL('image/png');
        if (!isCancelled) setMaskedPreviewUrl(dataUrl);
      } catch (err) {
        // Fallback to original preview on any error
        if (!isCancelled) setMaskedPreviewUrl(null);
      } finally {
        if (maskObjectUrl) URL.revokeObjectURL(maskObjectUrl);
      }
    };

    buildMaskedPreview();

    return () => {
      isCancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasImage, hasMask, textboxUploadedImages[0]?.preview, textboxUploadedImages[0]?.maskBlob]);

  // Ensure hooks run before any conditional returns
  if (!isOpen && !isPreview) return null;

  const Panel = (
    <div className={`rounded-xl border border-white/20 bg-gradient-to-br from-[#1a1a2e]/98 via-[#16213e]/98 to-[#0f1419]/98 backdrop-blur-3xl shadow-2xl ${isPreview ? 'w-42 sm:w-80 lg:w-64' : 'w-[90vw] sm:w-[28rem] md:w-[32rem] max-w-md'} overflow-hidden`}>
      {/* Compact Header */}
      <div className="relative bg-gradient-to-r from-blue-600/10 via-purple-600/10 to-blue-600/10 border-b border-white/10 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-purple-600 shadow-lg">
            <FaImage className="text-white text-sm" />
          </div>
          <div>
            <h3 className="text-base font-bold text-white">Product Photo</h3>
            <p className="text-xs text-gray-400">{hasImage ? 'Image ready' : 'Upload to continue'}</p>
          </div>
        </div>
        {!isPreview && (
          <button 
            type="button" 
            className="text-gray-400 hover:text-white hover:bg-white/10 rounded-lg p-1.5 transition-all duration-200" 
            onClick={onClose} 
            title="Close"
          >
            <FaTimes className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Content Area */}
      <div className="p-4">
        {/* Hidden file inputs */}
        <input
          type="file"
          accept="image/jpeg,image/jpg,image/png"
          onChange={handleTextboxFileUpload}
          className="hidden"
          ref={textboxFileInputRef}
          id="textboxFileInputPill"
        />
        <input
          type="file"
          accept="image/jpeg,image/jpg,image/png"
          onChange={handleReplaceFileUpload}
          className="hidden"
          ref={replaceFileInputRef}
          id="replaceFileInputPill"
        />

        {/* Upload/Preview Area */}
        {!hasImage ? (
          <>
            {/* Empty State - Compact Upload Zone */}
            <div
              className="relative group transition-all duration-300 border-2 border-dashed border-gray-600/50 hover:border-blue-500/70 rounded-xl bg-gradient-to-br from-gray-800/20 to-gray-900/20 hover:bg-blue-500/5 cursor-pointer overflow-hidden"
              onClick={() => textboxFileInputRef?.current?.click()}
              onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
              onDrop={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleTextboxFileUpload({ target: { files: e.dataTransfer?.files || [] } });
              }}
              title="Upload an image"
            >
              <div className="py-8 px-4 text-center">
                <div className="mb-3 inline-flex items-center justify-center w-14 h-14 rounded-xl bg-gradient-to-br from-blue-500/20 to-purple-600/20 border border-blue-500/30 group-hover:from-blue-500/30 group-hover:to-purple-600/30 transition-all duration-300">
                  <FaCloudUploadAlt className="text-2xl text-blue-400 group-hover:text-blue-300 transition-colors" />
                </div>
                <h4 className="text-sm font-semibold text-white mb-1">
                  Drop image here
                </h4>
                <p className="text-xs text-gray-400 mb-3">
                  or click to browse
                </p>
                <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-600/20 border border-blue-500/30 text-xs text-blue-300">
                  <FaImage className="text-xs" />
                  <span>JPEG, PNG</span>
                </div>
              </div>
            </div>
            
            {/* Error Message */}
            {textboxUploadError && (
              <div className="mt-3 p-2.5 rounded-lg bg-red-500/10 border border-red-500/30 text-red-400 text-xs flex items-start gap-2">
                <span>⚠️</span>
                <span>{textboxUploadError}</span>
              </div>
            )}
          </>
        ) : (
          <>
            {/* Image Preview with Overlay Actions */}
            <div className="relative rounded-xl overflow-hidden bg-black/20 group">
              <img
                src={(hasMask && maskedPreviewUrl) ? maskedPreviewUrl : textboxUploadedImages[0]?.preview}
                alt="Product"
                className="w-full h-64 sm:h-72 object-contain"
              />
              
              {/* Action Buttons - Always Visible on Mobile, Hover on Desktop */}
              <div className="absolute top-2 right-2 flex gap-1.5 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity duration-200">
                <button
                  type="button"
                  onClick={handleReplace}
                  className="p-2 rounded-lg bg-gray-900/90 hover:bg-gray-800 backdrop-blur-sm border border-white/20 text-white shadow-lg transition-all duration-200 hover:scale-105"
                  title="Replace image"
                >
                  <FaSyncAlt className="w-3.5 h-3.5" />
                </button>
                {!disableMasking && instagramPostType !== 'reel' && (
                  <button
                    type="button"
                    onClick={() => setIsMaskEditorOpen(true)}
                    className={`p-2 rounded-lg backdrop-blur-sm border shadow-lg transition-all duration-200 hover:scale-105 ${
                      hasMask 
                        ? 'bg-purple-600/90 hover:bg-purple-500 border-purple-400/30' 
                        : 'bg-purple-700/90 hover:bg-purple-600 border-purple-500/30'
                    } text-white`}
                    title={hasMask ? 'Edit mask' : 'Add mask'}
                  >
                    <FaPaintBrush className="w-3.5 h-3.5" />
                  </button>
                )}
                {hasMask && instagramPostType !== 'reel' && (
                  <button
                    type="button"
                    onClick={handleClearMask}
                    className="p-2 rounded-lg bg-gray-700/90 hover:bg-gray-600 backdrop-blur-sm border border-white/20 text-white shadow-lg transition-all duration-200 hover:scale-105"
                    title="Clear mask"
                  >
                    <FaEraser className="w-3.5 h-3.5" />
                  </button>
                )}
                <button
                  type="button"
                  onClick={handleRemove}
                  className="p-2 rounded-lg bg-red-600/90 hover:bg-red-500 backdrop-blur-sm border border-red-400/30 text-white shadow-lg transition-all duration-200 hover:scale-105"
                  title="Remove image"
                >
                  <FaTrash className="w-3.5 h-3.5" />
                </button>
              </div>
              
              {/* Status Badge */}
              {hasMask && (
                <div className="absolute bottom-2 left-2 px-2 py-1 rounded-md bg-purple-600/90 backdrop-blur-sm border border-purple-400/30 text-white text-xs font-medium flex items-center gap-1">
                  <FaPaintBrush className="w-3 h-3" />
                  <span>Masked</span>
                </div>
              )}
            </div>

            {/* Action Bar for Mobile */}
            <div className="sm:hidden mt-3 grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={handleReplace}
                className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-gray-700 hover:bg-gray-600 text-white text-xs font-medium transition-all duration-200"
              >
                <FaSyncAlt className="w-3 h-3" />
                Replace
              </button>
              {!disableMasking && instagramPostType !== 'reel' && (
                <button
                  type="button"
                  onClick={() => setIsMaskEditorOpen(true)}
                  className={`flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg text-white text-xs font-medium transition-all duration-200 ${
                    hasMask ? 'bg-purple-600 hover:bg-purple-500' : 'bg-purple-700 hover:bg-purple-600'
                  }`}
                >
                  <FaPaintBrush className="w-3 h-3" />
                  {hasMask ? 'Edit Mask' : 'Add Mask'}
                </button>
              )}
            </div>
          </>
        )}
      </div>

      {/* Footer - Done Button */}
      {hasImage && !isPreview && (
        <div className="border-t border-white/10 px-4 py-3 bg-gradient-to-t from-gray-900/50 to-transparent">
          <button
            type="button"
            onClick={onClose}
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg font-semibold bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white shadow-lg shadow-blue-500/30 transition-all duration-200 hover:scale-[1.02]"
          >
            <FaCheckCircle className="w-4 h-4" />
            <span>Done</span>
          </button>
        </div>
      )}
    </div>
  );

  // ========================================
  // RENDER: Main Modal UI
  // ========================================
  
  if (isPreview) {
    return (
      <div className={`absolute z-50 pointer-events-auto ${className}`}>
        {Panel}
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4">
      {/* Backdrop with blur */}
      <div 
        className="absolute inset-0 bg-gradient-to-br from-black/80 via-gray-900/80 to-black/80 backdrop-blur-sm" 
        onClick={onClose} 
      />
      
      {/* Modal Panel */}
      <div className="relative z-10 w-full flex items-center justify-center">
        {Panel}
      </div>
      
      {/* Product Mask Editor */}
      <ProductMaskEditor
        isOpen={isMaskEditorOpen}
        file={textboxUploadedImages[0]?.file}
        onClose={() => setIsMaskEditorOpen(false)}
        onConfirm={handleMaskConfirm}
        initialMaskBlob={textboxUploadedImages[0]?.maskBlob}
        initialMaskOriginalSize={textboxUploadedImages[0]?.maskOriginalSize}
      />

    </div>
  );
};

export default ReferenceImageModal; 